import type { JSX } from 'preact';
export type IconName =
  | 'drop' | 'history' | 'settings' | 'plus' | 'link' | 'close' | 'arrow' | 'check' | 'copy' | 'file'
  | 'download' | 'share' | 'lock' | 'chevron' | 'phone' | 'laptop' | 'text' | 'folder' | 'moon'
  | 'sun' | 'volume' | 'wifi' | 'select' | 'arrowDown' | 'refresh' | 'external' | 'clock';
const paths: Record<IconName, JSX.Element> = {
  drop: <><circle cx="12" cy="12" r="8.5" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1.5" fill="currentColor" stroke="none" /></>,
  history: <><path d="M4 8a8 8 0 1 1-1 7M4 3v5h5" /><path d="M12 7v5l3 2" /></>,
  settings: <><path d="m9 4 .6-2h4.8l.6 2 2 .9 2.1-.5 2.4 4.2-1.5 1.5v2.8l1.5 1.5-2.4 4.2-2.1-.5-2 .9-.6 2H9l-.6-2-2-.9-2.1.5L1.9 15l1.5-1.5v-2.8L1.9 9.2l2.4-4.1 2.1.5Z" transform="translate(1 1) scale(.9)" /><circle cx="12" cy="12" r="3" /></>,
  plus: <path d="M12 5v14M5 12h14" />,
  link: <><path d="m10 13 4-4M8 15l-1 1a3.5 3.5 0 0 1-5-5l5-5a3.5 3.5 0 0 1 5 0m0 12a3.5 3.5 0 0 0 5 0l5-5a3.5 3.5 0 0 0-5-5l-1 1" transform="translate(1 0) scale(.95)" /></>,
  close: <path d="m6 6 12 12M6 18 18 6" />,
  arrow: <path d="M5 12h14m-6-6 6 6-6 6" />,
  check: <path d="m5 12 4 4L19 6" />,
  copy: <><rect x="8" y="8" width="12" height="13" rx="3" /><path d="M15 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h3" /></>,
  file: <><path d="M14 3H6a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9Z" /><path d="M14 3v6h6M8 13h8M8 17h5" /></>,
  download: <><path d="M12 3v12m-5-5 5 5 5-5M4 16v4h16v-4" /></>,
  share: <><path d="M12 15V2m-4 4 4-4 4 4M7 9H4v12h16V9h-3" /></>,
  lock: <><rect x="5" y="10" width="14" height="11" rx="3" /><path d="M8 10V7a4 4 0 0 1 8 0v3" /><path d="M12 14v3" /></>,
  chevron: <path d="m9 5 7 7-7 7" />,
  phone: <><rect x="6" y="2" width="12" height="20" rx="3" /><path d="M10 18h4" /></>,
  laptop: <><rect x="4" y="3" width="16" height="13" rx="2" /><path d="m4 16-2 4h20l-2-4M9 20h6" /></>,
  text: <><path d="M4 5h16M12 5v15M8 20h8M4 5v3M20 5v3" /></>,
  folder: <path d="M3 7V4h7l3 3h8v13H3V7Z" />,
  moon: <path d="M20 14A8.5 8.5 0 0 1 10 3a8.5 8.5 0 1 0 10 11Z" />,
  sun: <><circle cx="12" cy="12" r="4" /><path d="M12 2v2m0 16v2M2 12h2m16 0h2M5 5l1 1m12 12 1 1M5 19l1-1M18 6l1-1" /></>,
  volume: <><path d="M11 4 6 8H3v8h3l5 4ZM15 8a6 6 0 0 1 0 8m3-11a10 10 0 0 1 0 14" /></>,
  wifi: <><path d="M3 8a14 14 0 0 1 18 0M6 12a9 9 0 0 1 12 0m-9 4a4 4 0 0 1 6 0" /><circle cx="12" cy="20" r="1" fill="currentColor" stroke="none" /></>,
  select: <><circle cx="12" cy="12" r="9" /><path d="m7 12 3 3 7-7" /></>,
  arrowDown: <path d="M12 4v16m-6-6 6 6 6-6" />,
  refresh: <><path d="M20 7a9 9 0 0 0-15-2L2 8m0-5v5h5m-3 9a9 9 0 0 0 15 2l3-3m0 5v-5h-5" /></>,
  external: <><path d="M14 3h7v7m0-7L10 14M10 3H3v18h18v-7" /></>,
  clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 3" /></>
};
export function Icon({
  name,
  size = 20,
  ...props
}: {
  name: IconName;
  size?: number;
} & JSX.IntrinsicElements['svg']) {
  return <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.65" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" {...props}>{paths[name]}</svg>;
}
export const animals = [
  'otter', 'fox', 'rabbit', 'hare', 'bear', 'cat', 'owl',
  'panda', 'robin', 'koala', 'dolphin', 'badger', 'raven', 'lynx',
] as const;

export type AnimalName = typeof animals[number];

export function resolveAnimal(animal?: string, name?: string): AnimalName {
  if (name) {
    const lower = name.toLowerCase();
    for (const a of animals) {
      if (lower.split(/[^a-z]+/).includes(a)) return a;
    }
  }
  if (animal) {
    const clean = animal.toLowerCase().trim() as AnimalName;
    if (animals.includes(clean)) return clean;
  }
  return 'otter';
}

export function Avatar({
  animal,
  name,
  color = '#d9e7fc',
  className = '',
  size = 64,
}: {
  animal?: string;
  name?: string;
  color?: string;
  className?: string;
  size?: number;
}) {
  const resolved = resolveAnimal(animal, name);

  let face: JSX.Element;
  let eyeY = 34;

  switch (resolved) {
    case 'fox':
      face = (
        <>
          <path d="m15 31 1-18 13 10h6l13-10 1 18-6 17-11 6-12-6Z" fill="#bb764d" />
          <path d="m15 31 17 10 17-10-6 17-11 6-12-6Z" fill="#fff1dd" />
          <path d="m19 20 7 6-6 3m25-9-7 6 6 3" fill="#5a3b36" />
          <path d="m28 40 4 4 4-4" fill="#4d3832" />
        </>
      );
      break;
    case 'rabbit':
      face = (
        <>
          <ellipse cx="24" cy="21" rx="6" ry="16" transform="rotate(-10 24 21)" fill="#f1eee6" />
          <ellipse cx="40" cy="21" rx="6" ry="16" transform="rotate(10 40 21)" fill="#f1eee6" />
          <path d="M24 12v16m16-16v16" stroke="#d9b9b4" stroke-width="3" />
          <ellipse cx="32" cy="39" rx="20" ry="17" fill="#f1eee6" />
          <path d="m29 43 3 3 3-3" fill="#bb8f8b" />
        </>
      );
      break;
    case 'hare':
      face = (
        <>
          <ellipse cx="23" cy="18" rx="5.5" ry="17" transform="rotate(-7 23 18)" fill="#cb9a66" />
          <path d="M20 2c2 5 7 5 7 0Z" fill="#2d2218" transform="rotate(-7 23 18)" />
          <path d="M23 10v14" stroke="#e0b890" stroke-width="2.5" transform="rotate(-7 23 18)" />
          <ellipse cx="41" cy="18" rx="5.5" ry="17" transform="rotate(7 41 18)" fill="#cb9a66" />
          <path d="M38 2c2 5 7 5 7 0Z" fill="#2d2218" transform="rotate(7 41 18)" />
          <path d="M41 10v14" stroke="#e0b890" stroke-width="2.5" transform="rotate(7 41 18)" />
          <ellipse cx="32" cy="39" rx="19" ry="17" fill="#cb9a66" />
          <ellipse cx="32" cy="45" rx="10" ry="7.5" fill="#f5ede2" />
          <path d="m29 42 3 3 3-3" fill="#8a5342" />
          <path d="m13 41 8 1.5m-8 4 8-.8m30-4.7-8 1.5m8 4-8-.8" stroke="#875836" stroke-width="1.1" stroke-linecap="round" />
        </>
      );
      break;
    case 'cat':
      face = (
        <>
          <path d="m13 34 2-22 15 10h5l14-10 2 22c8 27-46 27-38 0" fill="#898f9a" />
          <path d="m18 20 7 6-7 3m27-9-7 6 7 3" fill="#d3bdc0" />
          <ellipse cx="32" cy="45" rx="11" ry="8" fill="#ede8df" />
          <path d="m29 41 3 3 3-3" fill="#5b5258" />
          <path d="m10 40 12 2m-11 5 12-1m31-6-12 2m11 5-12-1" stroke="#555d6b" stroke-width="1.4" />
        </>
      );
      break;
    case 'owl':
      eyeY = 35;
      face = (
        <>
          <path d="m14 27-1-12 13 7h12l13-7-1 12c12 41-48 41-36 0" fill="#9a8471" />
          <path d="M32 30c-13-15-27 10-7 15l7-4 7 4c20-5 6-30-7-15" fill="#f3e8d4" />
          <path d="m29 39 3 7 3-7" fill="#c99851" />
        </>
      );
      break;
    case 'bear':
      face = (
        <>
          <circle cx="17" cy="22" r="9" fill="#8e684d" />
          <circle cx="47" cy="22" r="9" fill="#8e684d" />
          <circle cx="17" cy="22" r="4.5" fill="#b99377" />
          <circle cx="47" cy="22" r="4.5" fill="#b99377" />
          <ellipse cx="32" cy="38" rx="22" ry="20" fill="#8e684d" />
          <ellipse cx="32" cy="45" rx="11" ry="9.5" fill="#ebd8bd" />
          <path d="M27 41q5-4 10 0l-5 5Z" fill="#3e3128" />
          <path d="M32 46v2.5m-5-.5q5 4 10 0" fill="none" stroke="#3e3128" stroke-width="1.4" stroke-linecap="round" />
        </>
      );
      break;
    case 'panda':
      face = (
        <>
          <circle cx="16" cy="20" r="8" fill="#25272a" />
          <circle cx="48" cy="20" r="8" fill="#25272a" />
          <ellipse cx="32" cy="38" rx="22" ry="19" fill="#faf8f5" />
          <ellipse cx="23" cy="34" rx="7" ry="5.5" transform="rotate(-18 23 34)" fill="#25272a" />
          <ellipse cx="41" cy="34" rx="7" ry="5.5" transform="rotate(18 41 34)" fill="#25272a" />
          <circle cx="24" cy="34" r="1.8" fill="#ffffff" />
          <circle cx="40" cy="34" r="1.8" fill="#ffffff" />
          <path d="M28 42q4-3 8 0l-4 4Z" fill="#25272a" />
          <path d="M32 46v2m-4-1q4 3 8 0" fill="none" stroke="#25272a" stroke-width="1.3" stroke-linecap="round" />
        </>
      );
      break;
    case 'koala':
      face = (
        <>
          <circle cx="13" cy="24" r="11" fill="#888f96" />
          <circle cx="51" cy="24" r="11" fill="#888f96" />
          <circle cx="14" cy="24" r="6" fill="#e8eaed" />
          <circle cx="50" cy="24" r="6" fill="#e8eaed" />
          <ellipse cx="32" cy="38" rx="20" ry="18" fill="#888f96" />
          <ellipse cx="21" cy="45" rx="6" ry="4" fill="#e8eaed" />
          <ellipse cx="43" cy="45" rx="6" ry="4" fill="#e8eaed" />
          <ellipse cx="32" cy="39" rx="6" ry="9" fill="#292c30" />
          <ellipse cx="32" cy="35" rx="2" ry="1.5" fill="#3f444a" opacity=".5" />
        </>
      );
      break;
    case 'dolphin':
      face = <>
        <path d="M26 24q-2-11 10-15l-1 16" fill="#719dad" />
        <path d="M13 38c-1-11 6-21 18-21 10 0 16 6 17 14l9 7q3 4-3 5l-13-1c-6 13-22 15-28-4Z" fill="#83adbb" />
        <path d="M16 38q9 11 25 0c-3 15-20 19-25 0Z" fill="#e6f0ef" />
        <path d="m23 43 1 12q8-3 9-12" fill="#719dad" />
        <path d="M39 38q7 3 14 1" fill="none" stroke="#426773" stroke-width="1.5" stroke-linecap="round" />
        <circle cx="36" cy="29" r="2.5" fill="#334a56" /><circle cx="36.6" cy="28.2" r=".8" fill="#fff" />
      </>;
      break;
    case 'badger':
      face = (
        <>
          <circle cx="17" cy="22" r="7" fill="#3a3d42" />
          <circle cx="47" cy="22" r="7" fill="#3a3d42" />
          <path d="M11 21a6 6 0 0 1 11-3" stroke="#f6f7f9" stroke-width="2" fill="none" />
          <path d="M42 18a6 6 0 0 1 11 3" stroke="#f6f7f9" stroke-width="2" fill="none" />
          <ellipse cx="32" cy="38" rx="21" ry="18" fill="#3a3d42" />
          <path d="M26 19h12l4 18-3 14h-14l-3-14Z" fill="#f6f7f9" />
          <path d="M19 22h7l-1 18h-6Z" fill="#1e2023" />
          <path d="M38 22h7l0 18h-6Z" fill="#1e2023" />
          <path d="M28 44q4-3 8 0l-4 4Z" fill="#1e2023" />
          <path d="M32 48v2" stroke="#1e2023" stroke-width="1.2" stroke-linecap="round" />
        </>
      );
      break;
    case 'robin':
      eyeY = 33;
      face = (
        <>
          <ellipse cx="32" cy="35" rx="20" ry="17" fill="#6d5e52" />
          <path d="M21 28q11-5 22 0c4 9 2 20-11 22-13-2-15-13-11-22Z" fill="#d9512c" />
          <path d="M26 48q6-2 12 0c-2 4-10 4-12 0Z" fill="#f0ebe1" />
          <path d="m30 36 2-4 2 4-2 3Z" fill="#2d251f" />
        </>
      );
      break;
    case 'raven':
      face = <>
        <path d="m21 23 1-11 6 6 7-6-1 11" fill="#5a657c" />
        <path d="M13 36c-1-12 6-20 17-20s18 8 18 19l-4 14-13 6-13-7Z" fill="#48536b" />
        <path d="M17 38q12 9 26 0l-4 11-9 4-10-7Z" fill="#68758f" />
        <path d="M38 29q11 1 17 10l-17 1-6-4Z" fill="#aab6c6" />
        <path d="m38 36 17 3-17 1" fill="#7e8c9f" />
        <ellipse cx="28" cy="29" rx="5" ry="5.5" fill="#d4dee8" />
        <circle cx="29" cy="29" r="2.5" fill="#30384b" /><circle cx="29.7" cy="28.2" r=".8" fill="#fff" />
        <path d="m20 43 5 4m3-3 5 4" stroke="#8b98ad" stroke-width="1.3" stroke-linecap="round" />
      </>;
      break;
    case 'lynx':
      face = (
        <>
          <path d="m14 34 3-18 10 9-4 13Z" fill="#be8b5a" />
          <path d="M17 16 15 7 19 14" stroke="#1f1812" stroke-width="2.2" stroke-linecap="round" />
          <path d="m50 34-3-18-10 9 4 13Z" fill="#be8b5a" />
          <path d="M47 16 49 7 45 14" stroke="#1f1812" stroke-width="2.2" stroke-linecap="round" />
          <ellipse cx="32" cy="38" rx="20" ry="17" fill="#be8b5a" />
          <path d="M11 40 16 35 12 46Z" fill="#f0e6da" />
          <path d="M53 40 48 35 52 46Z" fill="#f0e6da" />
          <ellipse cx="32" cy="45" rx="11" ry="7.5" fill="#f0e6da" />
          <path d="m29 42 3 3 3-3" fill="#9e5f56" />
          <circle cx="20" cy="40" r="1" fill="#54371f" />
          <circle cx="44" cy="40" r="1" fill="#54371f" />
          <path d="m11 42 10 1m-9 4 9-.8m33-4.2-10 1m9 4-9-.8" stroke="#54371f" stroke-width="1.2" stroke-linecap="round" />
        </>
      );
      break;
    case 'otter':
    default:
      face = (
        <>
          <circle cx="15" cy="27" r="5" fill="#7a583e" />
          <circle cx="49" cy="27" r="5" fill="#7a583e" />
          <circle cx="15" cy="27" r="2.5" fill="#c49b7e" />
          <circle cx="49" cy="27" r="2.5" fill="#c49b7e" />
          <ellipse cx="32" cy="37" rx="21" ry="18" fill="#7a583e" />
          <ellipse cx="32" cy="43" rx="14" ry="9" fill="#f2e4d0" />
          <path d="M28 39q4-3 8 0l-4 4Z" fill="#35251d" />
          <path d="M32 43v2.5m-5-.5q5 4 10 0" fill="none" stroke="#35251d" stroke-width="1.3" stroke-linecap="round" />
          <path d="m13 41 8 1.5m-8 4.5 8-1m30-5-8 1.5m8 4.5-8-1" stroke="#523927" stroke-width="1.2" stroke-linecap="round" />
        </>
      );
      break;
  }

  return (
    <span
      class={`avatar ${className}`}
      style={{
        '--avatar-color': ({ '#7c5cff': '#e6e5f4', '#009688': '#d7eeec', '#e76f51': '#f7e6d8', '#3a86ff': '#dce9fb', '#ef476f': '#f3dde5', '#06a77d': '#e7edde', '#f4a261': '#f7e6d8', '#8338ec': '#e6e5f4' } as Record<string, string>)[color] || color,
        width: `${size}px`,
        height: `${size}px`,
      }}
    >
      <svg viewBox="0 0 64 64" aria-hidden="true">
        {face}
        {!['panda', 'dolphin', 'raven'].includes(resolved) && (
          <>
            <circle cx="24" cy={eyeY} r="2" fill={resolved === 'badger' ? '#dfd7c4' : '#34332f'} />
            <circle cx="40" cy={eyeY} r="2" fill={resolved === 'badger' ? '#dfd7c4' : '#34332f'} />
          </>
        )}
      </svg>
    </span>
  );
}
